import React from 'react'
import {View} from 'react-native'
import {setStringAsync} from 'expo-clipboard'
import {type AppBskyActorDefs} from '@atproto/api'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {useRequireEmailVerification} from '#/lib/hooks/useRequireEmailVerification'
import {type NavigationProp} from '#/lib/routes/types'
import {getTellusProfile} from '#/lib/tellusProfileRecord'
import {useSetMessageDraft} from '#/state/messages/message-drafts'
import {useGetConvoAvailabilityQuery} from '#/state/queries/messages/get-convo-availability'
import {useAgent} from '#/state/session'
import * as Toast from '#/view/com/util/Toast'
import {atoms as a, select, useBreakpoints, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {Loader} from '#/components/Loader'
import {canBeMessaged} from '#/components/dms/util'
import {Message_Stroke2_Corner0_Rounded as Message} from '#/components/icons/Message'
import * as Menu from '#/components/Menu'
import {Text} from '#/components/Typography'
import openMatrixDM from '../../lib/openMatrixDM'

// Fallback to the stable Tellus Chat landing if no invite route is available.
const DEFAULT_INVITE_URL = 'https://tellus.chat'

type TellusActionState = {
  convoAvailability: ReturnType<typeof useGetConvoAvailabilityQuery>['data']
  buttonLabel: string
  dialogControl: Dialog.DialogControlProps
  hasMatrixId: boolean
  matrixId: string | null | undefined
  inviteCopyText: string
  inviteDraftText: string
  isOpeningChat: boolean
  onOpenDialog: () => void
  onOpenChat: () => void
  onCopyInvite: () => void
}

function useTellusMessageAction(
  profile: AppBskyActorDefs.ProfileViewDetailed,
): TellusActionState {
  const {_} = useLingui()
  const agent = useAgent()
  const navigation = useNavigation<NavigationProp>()
  const requireEmailVerification = useRequireEmailVerification()
  const {data: convoAvailability} = useGetConvoAvailabilityQuery(profile.did)
  const setMessageDraft = useSetMessageDraft()
  const dialogControl = Dialog.useDialogControl()
  const [tellusMatrixId, setTellusMatrixId] = React.useState<
    string | null | undefined
  >(undefined)
  const [tellusStatus, setTellusStatus] = React.useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle')
  const [isOpeningChat, setIsOpeningChat] = React.useState(false)

  const normalizedHandle = React.useMemo(() => {
    const handle = profile?.handle?.trim() || ''
    if (!handle) return ''
    return handle.startsWith('@') ? handle : `@${handle}`
  }, [profile?.handle])

  const inviteUrl = React.useMemo(() => {
    if (!normalizedHandle) return DEFAULT_INVITE_URL
    const handleParam = encodeURIComponent(normalizedHandle.replace(/^@/, ''))
    return `${DEFAULT_INVITE_URL}/invite?handle=${handleParam}`
  }, [normalizedHandle])

  const inviteCopyText = React.useMemo(() => {
    if (normalizedHandle) {
      return _(
        msg`Rejoins-moi sur Tellus Chat pour discuter avec ${normalizedHandle} : ${inviteUrl}`,
      )
    }
    return _(msg`Rejoins-moi sur Tellus Chat : ${inviteUrl}`)
  }, [_, normalizedHandle, inviteUrl])

  const inviteDraftText = React.useMemo(
    () => _(msg`Salut 👋 Je t’invite à discuter sur Tellus Chat.`),
    [_],
  )

  React.useEffect(() => {
    let mounted = true
    if (!convoAvailability?.canChat || !profile?.did) return
    setTellusStatus('loading')
    getTellusProfile(profile.did, agent)
      .then(result => {
        if (!mounted) return
        setTellusMatrixId(result?.matrixId ?? null)
        setTellusStatus('ready')
      })
      .catch(err => {
        if (!mounted) return
        console.error(err)
        setTellusStatus('error')
      })
    return () => {
      mounted = false
    }
  }, [agent, convoAvailability?.canChat, profile?.did])

  const ensureMatrixId = React.useCallback(async () => {
    if (tellusStatus === 'ready') return tellusMatrixId ?? null
    setTellusStatus('loading')
    const result = await getTellusProfile(profile.did, agent)
    const matrixId = result?.matrixId ?? null
    setTellusMatrixId(matrixId)
    setTellusStatus('ready')
    return matrixId
  }, [agent, profile?.did, tellusMatrixId, tellusStatus])

  const onOpenChat = React.useCallback(async () => {
    if (!convoAvailability?.canChat) {
      return
    }

    try {
      setIsOpeningChat(true)
      let matrixId = tellusMatrixId
      if (tellusStatus !== 'ready') {
        matrixId = await ensureMatrixId()
      }
      if (!matrixId) {
        throw new Error('Missing Matrix ID')
      }
      const roomId = await openMatrixDM(matrixId, navigation, normalizedHandle, {
        suppressInviteNavigation: true,
      })
      if (!roomId) return
      setMessageDraft(roomId, inviteDraftText)
      dialogControl.close()
    } catch (e) {
      console.error(e)
      setTellusStatus('error')
      Toast.show(_(msg`Impossible d’ouvrir le chat`))
    } finally {
      setIsOpeningChat(false)
    }
  }, [
    agent,
    convoAvailability,
    dialogControl,
    ensureMatrixId,
    inviteDraftText,
    navigation,
    normalizedHandle,
    profile?.did,
    setMessageDraft,
    tellusMatrixId,
    tellusStatus,
    _,
  ])

  const wrappedOpenChat = requireEmailVerification(onOpenChat, {
    instructions: [
      <Trans key="message">
        Before you can message another user, you must first verify your email.
      </Trans>,
    ],
  })

  const onCopyInvite = React.useCallback(() => {
    dialogControl.close(() => {
      setStringAsync(inviteCopyText)
        .then(() => {
          Toast.show(_(msg`Lien copié`), 'clipboard-check')
        })
        .catch(err => {
          console.error(err)
          Toast.show(_(msg`Impossible de copier le lien`))
        })
    })
  }, [dialogControl, inviteCopyText, _])

  const buttonLabel = normalizedHandle
    ? _(msg`Inviter ${normalizedHandle} sur Tellus Chat`)
    : _(msg`Inviter sur Tellus Chat`)

  return {
    convoAvailability,
    buttonLabel,
    dialogControl,
    hasMatrixId: tellusStatus === 'ready' && !!tellusMatrixId,
    matrixId: tellusMatrixId,
    inviteCopyText,
    inviteDraftText,
    isOpeningChat,
    onOpenDialog: dialogControl.open,
    onOpenChat: wrappedOpenChat,
    onCopyInvite,
  }
}

function TellusChatInviteDialog({
  control,
  hasMatrixId,
  matrixId,
  isOpeningChat,
  onOpenChat,
  onCopyInvite,
}: {
  control: Dialog.DialogControlProps
  hasMatrixId: boolean
  matrixId: string | null | undefined
  isOpeningChat: boolean
  onOpenChat: () => void
  onCopyInvite: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()

  return (
    <Dialog.Outer control={control} testID="tellusInviteDialog">
      <Dialog.Handle />
      <Dialog.ScrollableInner label={_(msg`Inviter sur Tellus Chat`)}>
        <View style={[a.gap_lg]}>
          <View style={[a.gap_sm]}>
            <Text style={[a.text_xl, a.font_heavy]}>
              <Trans>Inviter sur Tellus Chat</Trans>
            </Text>
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              <Trans>Envoyer une invitation à discuter via Tellus Chat.</Trans>
            </Text>
          </View>

          {hasMatrixId ? (
            <View style={[a.gap_xs]}>
              {matrixId ? (
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  {matrixId}
                </Text>
              ) : null}
            </View>
          ) : (
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              <Trans>
                Ce compte n’a pas encore de messagerie Tellus Chat activée.
              </Trans>
            </Text>
          )}

          <View style={[a.gap_sm]}>
            <Button
              label={
                hasMatrixId
                  ? _(msg`Ouvrir le chat`)
                  : _(msg`Copier un lien d’invitation`)
              }
              color="primary"
              size="large"
              variant="solid"
              disabled={hasMatrixId && isOpeningChat}
              onPress={hasMatrixId ? onOpenChat : onCopyInvite}
              style={[a.gap_sm]}>
              <ButtonText>
                {hasMatrixId ? (
                  <Trans>Ouvrir le chat</Trans>
                ) : (
                  <Trans>Copier un lien d’invitation</Trans>
                )}
              </ButtonText>
              {hasMatrixId && isOpeningChat ? <Loader size="sm" /> : null}
            </Button>
            <Button
              label={_(msg`Annuler`)}
              color="secondary"
              size="large"
              variant="ghost"
              onPress={control.close}>
              <ButtonText>
                <Trans>Annuler</Trans>
              </ButtonText>
            </Button>
          </View>
        </View>
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

export function MessageProfileButton({
  profile,
}: {
  profile: AppBskyActorDefs.ProfileViewDetailed
}) {
  const t = useTheme()
  const {gtMobile} = useBreakpoints()
  const {
    convoAvailability,
    buttonLabel,
    dialogControl,
    hasMatrixId,
    matrixId,
    isOpeningChat,
    onOpenDialog,
    onOpenChat,
    onCopyInvite,
  } = useTellusMessageAction(profile)

  const inviteButtonStyle = React.useMemo(() => {
    const borderColor = select(t.name, {
      light: 'rgba(255,255,255,0.22)',
      dim: 'rgba(255,255,255,0.10)',
      dark: 'rgba(255,255,255,0.10)',
    })
    const backgroundColor = select(t.name, {
      light: 'rgba(255,255,255,0.65)',
      dim: 'rgba(28,28,30,0.55)',
      dark: 'rgba(28,28,30,0.55)',
    })
    return [
      a.flex_shrink,
      a.flex_nowrap,
      a.justify_center,
      {
        height: 38,
        paddingHorizontal: 13,
        paddingVertical: 0,
        borderRadius: 9999,
        borderWidth: 1,
        borderColor,
        backgroundColor,
        gap: 8,
      },
      web({
        backdropFilter: 'blur(16px) saturate(180%)',
        WebkitBackdropFilter: 'blur(16px) saturate(180%)',
        boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
      }),
    ]
  }, [t.name])

  if (!convoAvailability) {
    // show pending state based on declaration
    if (canBeMessaged(profile)) {
      return (
        <View
          testID="dmBtnLoading"
          aria-hidden={true}
          style={[
            a.justify_center,
            a.align_center,
            t.atoms.bg_contrast_25,
            a.rounded_full,
            // Matches size of button below to avoid layout shift
            {width: 38, height: 38},
          ]}>
          <Message style={[t.atoms.text, {opacity: 0.3}]} size="md" />
        </View>
      )
    } else {
      return null
    }
  }

  if (convoAvailability.canChat) {
    return (
      <>
        <Button
          accessibilityRole="button"
          testID="dmBtn"
          size="small"
          color="secondary"
          variant="solid"
          label={buttonLabel}
          style={inviteButtonStyle}
          onPress={onOpenDialog}>
          <Message style={[t.atoms.text]} size="sm" />
          <ButtonText
            numberOfLines={1}
            ellipsizeMode="tail"
            style={[a.text_sm, a.font_medium, t.atoms.text, {minWidth: 0}]}>
            {gtMobile ? (
              <Trans>Inviter sur Tellus Chat</Trans>
            ) : (
              <Trans>Inviter</Trans>
            )}
          </ButtonText>
        </Button>
        <TellusChatInviteDialog
          control={dialogControl}
          hasMatrixId={hasMatrixId}
          matrixId={matrixId}
          isOpeningChat={isOpeningChat}
          onOpenChat={onOpenChat}
          onCopyInvite={onCopyInvite}
        />
      </>
    )
  } else {
    return null
  }
}

export function MessageProfileMenuItem({
  profile,
}: {
  profile: AppBskyActorDefs.ProfileViewDetailed
}) {
  const {
    convoAvailability,
    buttonLabel,
    dialogControl,
    hasMatrixId,
    matrixId,
    isOpeningChat,
    onOpenDialog,
    onOpenChat,
    onCopyInvite,
  } = useTellusMessageAction(profile)

  if (!convoAvailability?.canChat) {
    return null
  }

  return (
    <>
      <Menu.Item label={buttonLabel} onPress={onOpenDialog}>
        <Menu.ItemText>
          <Trans>Inviter sur Tellus Chat</Trans>
        </Menu.ItemText>
        <Menu.ItemIcon icon={Message} />
      </Menu.Item>
      <TellusChatInviteDialog
        control={dialogControl}
        hasMatrixId={hasMatrixId}
        matrixId={matrixId}
        isOpeningChat={isOpeningChat}
        onOpenChat={onOpenChat}
        onCopyInvite={onCopyInvite}
      />
    </>
  )
}
